import { Head, Link, router } from "@inertiajs/react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useState } from "react"

import {
  WorkflowStatusBadge,
  deriveWorkflowStatus,
  isActionableStatus,
} from "@/components/hpi"
import { useNavPermissions } from "@/hooks/use-nav-permissions"
import AppLayout from "@/layouts/app-layout"
import { formatUSDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface TaskItem {
  id: number
  task_group: "case" | "customer"
  due_date: string | null
  urgent: boolean
  category: string | null
  name: string
  name2: string | null
  title: string | null
  notes: string | null
  case_id: number | null
  customer_id: number | null
  event_id: number | null
}

interface ScheduledWorkTeamMember {
  name: string
  is_leader: boolean
}

interface ScheduledWorkEntry {
  id: number
  case_id: number
  case_name: string
  start_date: string
  end_date: string | null
  no_start: boolean
  title: string | null
  team: ScheduledWorkTeamMember[]
}

interface UpcomingEvent {
  id: number
  date: string
  description: string
}

interface UpcomingPto {
  id: number
  employee_name: string
  date_description: string
  user_id: number
}

interface TimeExpenseEntry {
  id: number
  entry_type: "time" | "expense"
  date_added: string
  case_id: number
  case_name: string
  description: string
  employee_name: string
  submitted: boolean
  approved: boolean
  billed: boolean
  paid: boolean
  was_rejected: boolean
  is_mine: boolean
}

interface OpenDailyReport {
  id: number
  case_id: number
  case_name: string
  report_date: string
  status: string
  status_key: "draft" | "submitted" | "approved"
}

interface CompanyOption {
  id: number
  name: string
}

interface Props {
  task_list: TaskItem[]
  scheduled_work: ScheduledWorkEntry[]
  needs_attention: NeedsAttentionData
  upcoming_events: UpcomingEvent[]
  upcoming_pto: UpcomingPto[]
  time_expenses: TimeExpenseEntry[]
  open_daily_reports: OpenDailyReport[]
  companies: CompanyOption[]
  selected_company_id: number
  selected_company_name: string | null
}

/* ── Helpers ────────────────────────────────────────────── */

function formatScheduleDate(dateStr: string, noStart = false): string {
  const d = new Date(dateStr)
  const date = d.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })
  const hours = d.getHours()
  const minutes = d.getMinutes()
  // Start time unknown flag, or the legacy convention where 1:00 AM means "time TBD"
  if (noStart || (hours === 1 && minutes === 0)) return `${date} TBD`
  return `${date} ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
}

function formatScheduleDay(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleDateString("en-US", { weekday: "short" })
}

/* ── Needs Attention Stats ──────────────────────────────── */

interface NeedsAttentionData {
  pending_pre_approval: number
  pending_final_approval: number
  pending_preparation: number
  pending_daily_reports: number
  unapproved_te: number
  unbilled_amount: number
}

function formatCurrency(amount: number): string {
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(1)}k`
  }
  return `$${amount.toFixed(0)}`
}

function NeedsAttention({ data }: { data: NeedsAttentionData }) {
  const stats = [
    {
      label: "Reports Pending",
      sub: "Pre-Approval",
      value: String(data.pending_pre_approval),
      href: "/reports/pending_approval",
    },
    {
      label: "Reports Pending",
      sub: "Final Approval",
      value: String(data.pending_final_approval),
      href: "/reports/pending_approval",
    },
    {
      label: "Reports Pending",
      sub: "Preparation",
      value: String(data.pending_preparation),
      href: "/reports/pending_preparation",
    },
    {
      label: "Unbilled",
      sub: "Time & Expenses",
      value:
        data.unbilled_amount > 0 ? formatCurrency(data.unbilled_amount) : "0",
      href: "/accounting/reports/unbilled",
    },
    {
      label: "Unapproved",
      sub: "Time/Expenses",
      value: String(data.unapproved_te),
      href: "/review/time-expenses",
    },
  ]

  return (
    <div className="mb-6">
      <h2 className="text-hpi-dark-gray mb-3 text-xl font-semibold tracking-wide">
        NEEDS ATTENTION
      </h2>
      <div className="flex gap-3">
        {stats.map((stat, i) => (
          <Link
            key={i}
            href={stat.href}
            className="border-hpi-light-gray min-w-[130px] flex-1 border-t-2 bg-white px-4 py-3 transition-colors hover:bg-gray-50"
          >
            <div className="text-hpi-active-blue text-[11px] leading-tight font-medium">
              {stat.label}
            </div>
            <div className="text-hpi-active-blue text-[11px] leading-tight font-medium">
              {stat.sub}
            </div>
            <div className="text-hpi-dark-blue mt-1 text-3xl font-light">
              {stat.value}
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}

/* ── Table Panel (reusable for all 3 panels) ────────────── */

interface TablePanelProps {
  title: string
  headers: { label: string; sub?: string }[]
  children: React.ReactNode
  pagination?: { start: number; end: number; total: number }
  onPrevPage?: () => void
  onNextPage?: () => void
  hasPrev?: boolean
  hasNext?: boolean
}

function TablePanel({
  title,
  headers,
  children,
  pagination,
  onPrevPage,
  onNextPage,
  hasPrev,
  hasNext,
}: TablePanelProps) {
  return (
    <div className="flex flex-col">
      {/* Blue header bar */}
      <div className="bg-hpi-dark-blue rounded-t px-3 py-2">
        <h3 className="text-xs font-semibold tracking-wider text-white uppercase">
          {title}
        </h3>
      </div>

      {/* Column headers */}
      <div className="border-hpi-light-gray flex border-b bg-white px-3 py-1.5">
        {headers.map((h, i) => (
          <div key={i} className="min-w-0 flex-1">
            <div className="text-hpi-active-blue text-[10px] leading-tight font-medium">
              {h.label}
            </div>
            {h.sub && (
              <div className="text-hpi-active-blue text-[10px] leading-tight font-medium">
                {h.sub}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Rows */}
      <div className="flex-1 bg-white">{children}</div>

      {/* Pagination */}
      {pagination && (
        <div className="text-hpi-dark-gray flex items-center justify-center gap-2 bg-white py-2 text-xs">
          <button
            onClick={onPrevPage}
            disabled={!hasPrev}
            className="disabled:cursor-default"
          >
            <ChevronLeft
              className={`h-4 w-4 ${hasPrev ? "text-hpi-active-blue" : "text-hpi-med-gray"}`}
            />
          </button>
          <span className="font-medium">
            {pagination.start}-{pagination.end} OF {pagination.total}
          </span>
          <button
            onClick={onNextPage}
            disabled={!hasNext}
            className="disabled:cursor-default"
          >
            <ChevronRight
              className={`h-4 w-4 ${hasNext ? "text-hpi-active-blue" : "text-hpi-med-gray"}`}
            />
          </button>
        </div>
      )}
    </div>
  )
}

/* ── Pending Tasks Panel ────────────────────────────────── */

function formatTaskDate(dateStr: string | null): string {
  if (!dateStr) return ""
  const d = new Date(dateStr)
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`
}

function taskHref(task: TaskItem): string {
  if (task.task_group === "case" && task.case_id) {
    return `/cases/${task.case_id}/logs/${task.id}`
  }
  if (task.task_group === "customer" && task.customer_id) {
    return `/customers/${task.customer_id}/logs/${task.id}`
  }
  return "#"
}

const PER_PAGE = 8

function PendingTasksPanel({ tasks }: { tasks: TaskItem[] }) {
  const [page, setPage] = useState(1)
  const totalPages = Math.ceil(tasks.length / PER_PAGE)
  const start = (page - 1) * PER_PAGE
  const end = Math.min(start + PER_PAGE, tasks.length)
  const displayed = tasks.slice(start, end)

  return (
    <TablePanel
      title="Task List"
      headers={[
        { label: "Due Date", sub: "Name" },
        { label: "Title", sub: "Notes" },
      ]}
      pagination={
        tasks.length > PER_PAGE
          ? { start: start + 1, end, total: tasks.length }
          : undefined
      }
      hasPrev={page > 1}
      hasNext={page < totalPages}
      onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
      onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
    >
      {displayed.length === 0 ? (
        <div className="text-hpi-med-gray px-3 py-6 text-center text-xs">
          No pending tasks
        </div>
      ) : (
        displayed.map((task) => (
          <Link
            key={`${task.task_group}-${task.id}`}
            href={taskHref(task)}
            className={`border-hpi-light-gray/50 flex border-b px-3 py-2 last:border-b-0 hover:bg-gray-50 ${task.urgent ? "bg-red-50" : ""}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-hpi-dark-gray text-xs font-medium">
                {formatTaskDate(task.due_date)}
              </div>
              <div className="text-hpi-med-blue text-[10px]">{task.name}</div>
              {task.name2 && (
                <div className="text-hpi-med-gray text-[10px]">
                  {task.name2}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-hpi-dark-gray text-xs">
                {task.category && <span>{task.category}</span>}
                {task.title && (
                  <span className="font-medium">{task.title}</span>
                )}
                {!task.category && !task.title && "—"}
              </div>
              <div className="text-hpi-med-blue truncate text-[10px]">
                {task.notes ?? ""}
              </div>
            </div>
          </Link>
        ))
      )}
    </TablePanel>
  )
}

/* ── Scheduled Work Panel ──────────────────────────────── */

function ScheduledWorkPanel({ entries }: { entries: ScheduledWorkEntry[] }) {
  const [page, setPage] = useState(1)
  const totalPages = Math.ceil(entries.length / PER_PAGE)
  const start = (page - 1) * PER_PAGE
  const end = Math.min(start + PER_PAGE, entries.length)
  const displayed = entries.slice(start, end)

  return (
    <TablePanel
      title="Scheduled Work"
      headers={[
        { label: "Day", sub: "Date" },
        { label: "Case", sub: "Team" },
        { label: "Title" },
      ]}
      pagination={
        entries.length > PER_PAGE
          ? { start: start + 1, end, total: entries.length }
          : undefined
      }
      hasPrev={page > 1}
      hasNext={page < totalPages}
      onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
      onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
    >
      {displayed.length === 0 ? (
        <div className="text-hpi-med-gray px-3 py-6 text-center text-xs">
          No scheduled work
        </div>
      ) : (
        displayed.map((entry) => {
          const leader = entry.team.find((t) => t.is_leader)
          const members = entry.team.filter((t) => !t.is_leader)
          const teamStr = [leader?.name, ...members.map((m) => m.name)]
            .filter(Boolean)
            .join(", ")

          return (
            <div
              key={entry.id}
              className="border-hpi-light-gray/50 flex border-b px-3 py-2 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <div className="text-hpi-dark-gray text-xs font-medium">
                  {formatScheduleDay(entry.start_date)}
                </div>
                <div className="text-hpi-med-blue text-[10px]">
                  {formatScheduleDate(entry.start_date, entry.no_start)}
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <Link
                  href={`/cases/${entry.case_id}/schedules/${entry.id}`}
                  className="text-hpi-dark-gray hover:text-hpi-active-blue text-xs font-semibold"
                >
                  {entry.case_name}
                </Link>
                <div className="text-hpi-med-blue truncate text-[10px]">
                  {teamStr || "No team assigned"}
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-hpi-dark-gray text-xs">
                  {entry.title ?? "—"}
                </div>
              </div>
            </div>
          )
        })
      )}
    </TablePanel>
  )
}

/* ── Open Daily Reports Panel ──────────────────────────── */

const reportStatusColors: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  submitted: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
}

function OpenDailyReportsPanel({ reports }: { reports: OpenDailyReport[] }) {
  const [page, setPage] = useState(1)
  const totalPages = Math.ceil(reports.length / PER_PAGE)
  const start = (page - 1) * PER_PAGE
  const end = Math.min(start + PER_PAGE, reports.length)
  const displayed = reports.slice(start, end)

  // Group displayed reports by case_id without assuming contiguous ordering
  const groupedMap = new Map<
    number,
    { case_id: number; case_name: string; items: OpenDailyReport[] }
  >()
  for (const r of displayed) {
    const existing = groupedMap.get(r.case_id)
    if (existing) {
      existing.items.push(r)
    } else {
      groupedMap.set(r.case_id, {
        case_id: r.case_id,
        case_name: r.case_name,
        items: [r],
      })
    }
  }
  const grouped = Array.from(groupedMap.values())

  return (
    <TablePanel
      title="My Open Daily Reports"
      headers={[{ label: "Case", sub: "Report Day" }, { label: "Status" }]}
      pagination={
        reports.length > PER_PAGE
          ? { start: start + 1, end, total: reports.length }
          : undefined
      }
      hasPrev={page > 1}
      hasNext={page < totalPages}
      onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
      onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
    >
      {displayed.length === 0 ? (
        <div className="text-hpi-med-gray px-3 py-6 text-center text-xs">
          No open daily reports
        </div>
      ) : (
        grouped.map((group) => (
          <div key={group.case_id}>
            {/* Case group header */}
            <div className="border-hpi-light-gray/50 border-b bg-gray-50 px-3 py-1.5">
              <Link
                href={`/cases/${group.case_id}/reports`}
                className="text-hpi-dark-gray hover:text-hpi-active-blue text-xs font-semibold"
              >
                {group.case_name}
              </Link>
            </div>
            {/* Report rows under this case */}
            {group.items.map((report) => (
              <Link
                key={report.id}
                href={`/cases/${report.case_id}/reports/${report.id}`}
                className="border-hpi-light-gray/50 flex items-center border-b px-3 py-2 pl-6 last:border-b-0 hover:bg-gray-50"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-hpi-dark-gray text-xs">
                    {formatUSDate(report.report_date)}
                  </div>
                </div>
                <div className="flex-none">
                  <span
                    className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${reportStatusColors[report.status_key] || reportStatusColors.draft}`}
                  >
                    {report.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ))
      )}
    </TablePanel>
  )
}

/* ── Time & Expenses Panel ──────────────────────────────── */

function TimeExpenseRow({ entry }: { entry: TimeExpenseEntry }) {
  const status = deriveWorkflowStatus(entry)
  const actionable = isActionableStatus(status)

  return (
    <Link
      href={`/cases/${entry.case_id}/${entry.entry_type === "expense" ? "expenses" : "time_entries"}/${entry.id}`}
      className={`border-hpi-light-gray/50 flex items-center border-b px-3 py-2 last:border-b-0 hover:bg-gray-50 ${
        actionable ? "border-l-2 border-l-orange-400" : ""
      } ${status === "rejected" ? "!border-l-red-400" : ""}`}
    >
      <div className="w-[80px] flex-none">
        <WorkflowStatusBadge status={status} />
      </div>
      <div className="min-w-0 flex-1 px-2">
        <div className="text-hpi-dark-gray truncate text-xs font-medium">
          {entry.case_name}
        </div>
        <div className="text-hpi-med-blue truncate text-[10px]">
          {entry.description}
        </div>
      </div>
      <div className="text-hpi-dark-gray w-[70px] flex-none text-right text-xs">
        {formatUSDate(entry.date_added)}
      </div>
    </Link>
  )
}

function TimeExpenseList({
  title,
  entries,
}: {
  title: string
  entries: TimeExpenseEntry[]
}) {
  const [page, setPage] = useState(1)
  const totalPages = Math.ceil(entries.length / PER_PAGE)
  const start = (page - 1) * PER_PAGE
  const end = Math.min(start + PER_PAGE, entries.length)
  const displayed = entries.slice(start, end)

  return (
    <TablePanel
      title={title}
      headers={[
        { label: "Status" },
        { label: "Case", sub: "Type" },
        { label: "Date" },
      ]}
      pagination={
        entries.length > PER_PAGE
          ? { start: start + 1, end, total: entries.length }
          : undefined
      }
      hasPrev={page > 1}
      hasNext={page < totalPages}
      onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
      onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
    >
      {displayed.length === 0 ? (
        <div className="text-hpi-med-gray px-3 py-6 text-center text-xs">
          No pending time & expenses
        </div>
      ) : (
        displayed.map((entry) => (
          <TimeExpenseRow
            key={`${entry.entry_type}-${entry.id}`}
            entry={entry}
          />
        ))
      )}
    </TablePanel>
  )
}

function TimeExpensesPanel({ entries }: { entries: TimeExpenseEntry[] }) {
  const myActionable = entries.filter(
    (e) => e.is_mine && isActionableStatus(deriveWorkflowStatus(e)),
  )
  const teamEntries = entries.filter(
    (e) =>
      !myActionable.some((m) => m.id === e.id && m.entry_type === e.entry_type),
  )
  const hasMySection = myActionable.length > 0 && teamEntries.length > 0

  if (hasMySection) {
    return (
      <div className="flex flex-col gap-4">
        <TimeExpenseList
          title={`Your Open Time & Expenses (${myActionable.length})`}
          entries={myActionable}
        />
        <TimeExpenseList
          title={`Team Open Time & Expenses (${teamEntries.length})`}
          entries={teamEntries}
        />
      </div>
    )
  }

  return (
    <TimeExpenseList
      title={`My Open Time & Expenses (${entries.length})`}
      entries={entries}
    />
  )
}

/* ── Upcoming Events Panel ──────────────────────────────── */

function UpcomingEventsPanel({ events }: { events: UpcomingEvent[] }) {
  if (events.length === 0) return null

  return (
    <div className="rounded border border-gray-200 bg-white shadow-sm">
      <h3 className="border-b border-gray-200 px-4 py-2.5 text-sm font-semibold text-[#374A66]">
        Upcoming Events
      </h3>
      <ul className="divide-y divide-dotted divide-gray-200 px-4 py-2">
        {events.map((event) => (
          <li key={event.id} className="py-2">
            <Link
              href={`/marketing/events/${event.id}`}
              className="text-sm text-gray-700 hover:text-[#374A66]"
            >
              {event.date} - {event.description}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ── Time Off Panel ──────────────────────────────────────── */

function TimeOffPanel({ entries }: { entries: UpcomingPto[] }) {
  if (entries.length === 0) return null

  return (
    <div className="rounded border border-gray-200 bg-white shadow-sm">
      <h3 className="border-b border-gray-200 px-4 py-2.5 text-sm font-semibold text-[#374A66]">
        Time Off{" "}
        <span className="ml-2 text-xs font-normal text-gray-400">
          Next 7 days
        </span>
      </h3>
      <ul className="divide-y divide-dotted divide-gray-200 px-4 py-2">
        {entries.map((entry) => (
          <li key={entry.id} className="py-2">
            <div className="text-sm text-gray-700">
              {entry.date_description} - {entry.employee_name}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ── Company Switcher ───────────────────────────────────── */

function CompanySwitcher({
  companies,
  selectedId,
  selectedName,
}: {
  companies: CompanyOption[]
  selectedId: number
  selectedName: string | null
}) {
  if (companies.length <= 1) return null

  function switchCompany(coId: number) {
    router.get("/dashboard", coId > 0 ? { co: coId } : {}, {
      preserveState: false,
    })
  }

  return (
    <div className="mb-4 flex items-center gap-3">
      <h2 className="text-hpi-dark-gray text-xl font-semibold tracking-wide">
        {selectedId > 0 ? selectedName : "Welcome"}
      </h2>
      <div className="flex items-center gap-1.5">
        <span className="text-hpi-med-gray text-xs">Company:</span>
        <button
          onClick={() => switchCompany(0)}
          className={`rounded px-2.5 py-1 text-xs font-medium ${
            selectedId === 0
              ? "bg-hpi-active-blue text-white"
              : "bg-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-active-blue/10"
          }`}
        >
          All
        </button>
        {companies.map((co) => (
          <button
            key={co.id}
            onClick={() => switchCompany(co.id)}
            className={`rounded px-2.5 py-1 text-xs font-medium ${
              selectedId === co.id
                ? "bg-hpi-active-blue text-white"
                : "bg-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-active-blue/10"
            }`}
          >
            {co.name}
          </button>
        ))}
      </div>
    </div>
  )
}

/* ── Main Dashboard ─────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Dashboard", href: "/dashboard" },
]

export default function Dashboard({
  task_list,
  scheduled_work,
  needs_attention,
  upcoming_events,
  upcoming_pto,
  time_expenses,
  open_daily_reports,
  companies,
  selected_company_id,
  selected_company_name,
}: Props) {
  const { isAdmin } = useNavPermissions()

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Dashboard" />

      <div className="p-6">
        <CompanySwitcher
          companies={companies}
          selectedId={selected_company_id}
          selectedName={selected_company_name}
        />

        {isAdmin && <NeedsAttention data={needs_attention} />}

        <div className="grid grid-cols-2 gap-4">
          <PendingTasksPanel tasks={task_list} />
          <ScheduledWorkPanel entries={scheduled_work} />
          <OpenDailyReportsPanel reports={open_daily_reports} />
          <TimeExpensesPanel entries={time_expenses} />
        </div>

        {(upcoming_events.length > 0 || upcoming_pto.length > 0) && (
          <div className="mt-4 grid grid-cols-2 gap-4">
            <UpcomingEventsPanel events={upcoming_events} />
            <TimeOffPanel entries={upcoming_pto} />
          </div>
        )}
      </div>
    </AppLayout>
  )
}
